import { apiGet, apiPost, apiPut } from "./client";
import type { RoutingUsageError } from "../helpers/routingUsage";

export type RouteDayIssue = {
  code: string;
  severity: "warning" | "error";
  minutesLate?: number;
};

export type RouteDayCustomer = {
  customerId?: string;
  name?: string;
  orderNumber?: string;
  companyId?: string;
};

export type RouteDayVisit = {
  visitId: string;
  driverId?: string | null;
  routeIds: string[];
  orderIds: string[];
  sequence?: number | null;
  orderStopNumber?: number | null;
  status: string;
  isPickup: boolean;
  location: {
    id?: string;
    displayName?: string;
    addressLine?: string;
    postCode?: string;
    city?: string;
    countryCode?: string;
    placeId?: string;
    coordinate?: { lat?: number; lon?: number };
  };
  timeWindow: {
    earliest?: boolean;
    latest?: boolean;
    after?: string;
    before?: string;
  };
  constraints?: {
    requiredTimeWindows: Array<{ startAt: string; endAt: string }>;
    preferredTimeWindows: Array<{ startAt: string; endAt: string }>;
    schedulingPreference?: "ASAP" | "ALAP" | null;
  };
  plannedStartTime?: string | null;
  plannedEndTime?: string | null;
  serviceDurationSeconds: number;
  cargo: Array<{
    id?: string;
    quantity?: number;
    weight?: number;
    length?: number;
    width?: number;
    height?: number;
  }>;
  customers: RouteDayCustomer[];
  issues: RouteDayIssue[];
  assignment?: {
    id: string;
    visitIds: string[];
    orderIds: string[];
    cargoIds: string[];
  };
};

export type RouteDayOverview = {
  planningSchemaVersion?: number;
  workspaceId: string;
  date: string;
  timezone: string;
  fingerprint: string;
  generatedAt: string;
  visits: RouteDayVisit[];
  editableRouteIds: string[];
  summary: {
    orders?: number;
    routeStops: number;
    physicalVisits: number;
    assignedVisits: number;
    unassignedVisits: number;
    issues: number;
  };
};

export type RouteDayPlanVisit = {
  routeIds: string[];
  plannedStartTime?: string | null;
  plannedEndTime?: string | null;
  serviceDurationSeconds: number;
};

export type RouteDayPlan = {
  expectedFingerprint: string;
  planningSchemaVersion: 3 | 4;
  unchangedRouteIds?: string[];
  drivers: Array<{ driverId: string; visits: RouteDayPlanVisit[] }>;
  unassignedRouteIds: string[];
};

export type RouteDayPlanValidation = {
  valid: true;
  fingerprint: string;
  changedRouteStops: number;
  changedOrders: number;
};

export type RouteDayDriverEstimate = {
  provider: string;
  calculatedAt: string;
  departureTime: string;
  trafficAware?: boolean;
  cached: boolean;
  visits: Array<{
    visitId: string;
    arrivalTime: string;
    departureTime: string;
    legFromPrevious?: {
      distanceMeters: number;
      durationSeconds: number;
    } | null;
    issues: RouteDayIssue[];
  }>;
  metrics: {
    travelDistanceMeters: number;
    travelDurationSeconds: number;
    waitDurationSeconds: number;
    reservedDurationSeconds?: number;
    totalDurationSeconds: number;
  };
};

export type RouteDayEstimates = {
  errors?: Record<string, string>;
  errorDetails?: Record<string, RoutingUsageError>;
  workspaceId: string;
  date: string;
  timezone: string;
  drivers: Record<string, RouteDayDriverEstimate>;
};

export const getRouteDayOverview = async (
  token: string,
  workspaceId: string,
  date: string,
): Promise<RouteDayOverview> => {
  const response = await apiGet(
    token,
    `/api/routes/date/${date}/overview`,
    workspaceId,
  );
  return response.data?.overview;
};

export const validateRouteDayPlan = async (
  token: string,
  workspaceId: string,
  date: string,
  plan: RouteDayPlan,
): Promise<RouteDayPlanValidation> => {
  const response = await apiPost(
    token,
    `/api/routes/date/${date}/plan/validate`,
    plan,
    workspaceId,
  );
  return response.data?.validation;
};

export const saveRouteDayPlan = async (
  token: string,
  workspaceId: string,
  date: string,
  plan: RouteDayPlan,
): Promise<RouteDayOverview> => {
  const response = await apiPut(
    token,
    `/api/routes/date/${date}/plan`,
    plan,
    workspaceId,
  );
  return response.data?.overview;
};

export const calculateRouteDayEstimates = async (
  token: string,
  workspaceId: string,
  date: string,
  driverIds: string[],
  departureTimes: Record<string, string> = {},
): Promise<RouteDayEstimates> => {
  const response = await apiPost(
    token,
    `/api/routes/date/${date}/estimates`,
    { driverIds, departureTimes },
    workspaceId,
  );
  return response.data?.estimates;
};

export const getRouteOptimizationCapability = async (
  token: string,
  workspaceId: string,
) => {
  const response = await apiGet(
    token,
    "/api/routes/optimization/capability",
    workspaceId,
  );
  return response.data?.capability;
};

export const previewOptimizedDriverRoute = async (
  token: string,
  workspaceId: string,
  driverId: string,
  date: string,
) => {
  const response = await apiPost(
    token,
    `/api/routes/driver/${encodeURIComponent(driverId)}/date/${date}/optimize`,
    { serviceDurationSeconds: 600 },
    workspaceId,
  );
  return response.data?.optimization;
};
